/**
 * Project-level domain context frontmatter (spec §1 of
 * `local-context-storage`).
 *
 * Lives at `iamu/context.yml` as YAML frontmatter + optional markdown
 * body. Mirrors the cloud `projects.context` JSONB shape PLUS the
 * separate `terminology` (glossary) table — the local file is a single
 * orientation surface; the cloud handler splits the payload on push.
 *
 * Nullish / optional defaults throughout: context is strictly additive
 * — the brainstorm helpers fall back gracefully when fields are
 * missing, so users can fill in what they know and leave the rest.
 */
import { z } from "zod";
import { authorityLevelEnum } from "../../types/enums.js";
import { SCHEMA_VERSION } from "./version.js";

/**
 * One regulation / standard / policy the project is governed by.
 * Mirrors cloud `projects.context.governedBy[]`.
 */
export const contextGovernedBySchema = z.object({
  name: z
    .string()
    .min(1)
    .max(500)
    .describe(
      "Name of the regulation / standard / law. Example: 'AFS 2023:2', 'ISO 45001', 'GDPR'.",
    ),
  authority_level: authorityLevelEnum.describe(
    "Authority tier — legislation / regulation / standard / company_policy / guideline / informational.",
  ),
});
export type ContextGovernedBy = z.infer<typeof contextGovernedBySchema>;

/**
 * One domain actor / role. Mirrors cloud `projects.context.actors[]`,
 * without the cloud-only `software_boundary` field (that lives in a
 * separate `boundaries[]` section on cloud; local doesn't surface it
 * as a first-class concept yet).
 */
export const contextActorSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(500)
    .describe("Role title or actor name. Example: 'Safety Manager'."),
  role: z
    .string()
    .min(1)
    .max(2048)
    .describe("What this actor does in the domain."),
});
export type ContextActor = z.infer<typeof contextActorSchema>;

/**
 * One glossary entry. Mirrors a cloud `terminology` row — the local
 * file flattens the cloud's separate table into an embedded array for
 * single-file orientation; the cloud handler splits on ingest.
 */
export const contextGlossaryEntrySchema = z.object({
  term: z.string().min(1).max(500).describe("Canonical term."),
  definition: z
    .string()
    .max(4096)
    .nullish()
    .describe("Short definition. Supports markdown inline if rendered later."),
  variants: z
    .array(z.string().min(1).max(500))
    .max(32)
    .optional()
    .describe(
      "Alternative spellings / abbreviations / synonyms so prompts see the term's whole surface.",
    ),
  translation: z
    .string()
    .max(500)
    .nullish()
    .describe(
      "Optional cross-language translation when the project spans jurisdictions.",
    ),
});
export type ContextGlossaryEntry = z.infer<typeof contextGlossaryEntrySchema>;

/**
 * Project-level domain context. Fields are all optional; the brainstorm
 * prompts build grounding from whatever's present.
 */
export const contextFrontmatterSchema = z.object({
  schema_version: z
    .literal(SCHEMA_VERSION)
    .describe(
      "Frontmatter schema version. Readers throw SchemaVersionError on mismatch.",
    ),
  domain: z
    .string()
    .max(1024)
    .nullish()
    .describe(
      "The domain this project operates in. Example: 'workplace safety management', 'payment processing'.",
    ),
  industry: z
    .string()
    .max(1024)
    .nullish()
    .describe(
      "Industry category. Example: 'manufacturing', 'fintech', 'healthcare'.",
    ),
  jurisdiction: z
    .string()
    .max(1024)
    .nullish()
    .describe(
      "Country / region the regulations and policies apply in. Example: 'Sweden', 'EU', 'United States'.",
    ),
  architecture: z
    .string()
    .max(8192)
    .nullish()
    .describe(
      "High-level architecture notes for the software. Brief is fine — this is orientation, not spec.",
    ),
  notes: z
    .string()
    .max(16_384)
    .nullish()
    .describe(
      "Additional orientation context that doesn't fit the structured fields. Extended multi-paragraph notes belong in the file body BELOW the frontmatter, not here.",
    ),
  access: z
    .string()
    .max(4096)
    .nullish()
    .describe(
      "Deprecated: belongs in `iamu/testability.md` `access` — how to switch user is test setup, not domain. Still read as a fallback when testability.md has none.",
    ),
  governed_by: z
    .array(contextGovernedBySchema)
    .max(64)
    .optional()
    .describe(
      "Regulations / standards / policies the domain is governed by, ordered roughly by authority.",
    ),
  actors: z
    .array(contextActorSchema)
    .max(64)
    .optional()
    .describe("Key domain roles + their responsibilities."),
  glossary: z
    .array(contextGlossaryEntrySchema)
    .max(512)
    .optional()
    .describe(
      "Domain-specific terms the project uses. Flattened into one file locally; the cloud handler splits into the terminology table on push.",
    ),
});
export type ContextFrontmatter = z.infer<typeof contextFrontmatterSchema>;

/**
 * A parsed context file — frontmatter + optional markdown `notes_body`
 * lifted from BELOW the YAML block. Long-form narrative belongs in the
 * body so YAML stays scannable.
 */
export interface LocalContext {
  frontmatter: ContextFrontmatter;
  notes_body: string;
}

/**
 * Empty context — default shape when `iamu/context.yml` is absent.
 * Consumers treat this as "unset" rather than "null" so they can
 * unconditionally walk the shape.
 */
export function emptyContext(): LocalContext {
  return {
    frontmatter: { schema_version: SCHEMA_VERSION },
    notes_body: "",
  };
}
